import React, { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config/apiConfig';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Divider,
    Chip,
    useMediaQuery,
    useTheme,
    Grid
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import {
    getTrainerVacations,
    createTrainerVacation,
    updateTrainerVacation,
    deleteTrainerVacation
} from '../api/trainerVacationApi';

export default function TrainerVacationModal({ open, onClose, affiliate, trainers, currentYear }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [vacations, setVacations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Form state
    const [selectedTrainer, setSelectedTrainer] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [editingVacation, setEditingVacation] = useState(null);
    
    const [currentUserId, setCurrentUserId] = useState(null);
    
    const userRole = localStorage.getItem('role');
    const isAffiliate = userRole === 'affiliate';
    const isTrainer = userRole === 'trainer';

    // Fetch current user ID
    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const token = localStorage.getItem('token');
                // API_URL imported from config
                const response = await fetch(`${API_URL}/auth/me`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                const data = await response.json();
                if (data.id) {
                    setCurrentUserId(data.id);
                    
                    // If trainer, pre-select their own ID
                    if (isTrainer && trainers.length > 0) {
                        const currentTrainer = trainers.find(t => t.trainerId === data.id);
                        if (currentTrainer) {
                            setSelectedTrainer(currentTrainer.trainerId.toString());
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to fetch current user:', err);
            }
        };
        
        if (open && trainers.length > 0) {
            fetchCurrentUser();
        }
    }, [open, trainers.length, isTrainer]);

    // Load vacations when modal opens
    useEffect(() => {
        if (open && affiliate?.id && currentYear) {
            loadVacations();
        }
    }, [open, affiliate?.id, currentYear]);

    const loadVacations = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await getTrainerVacations(affiliate.id, currentYear);
            setVacations(data);
        } catch (err) {
            setError(err.message || 'Failed to load vacations');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!selectedTrainer || !startDate || !endDate) {
            setError('Please fill in all fields');
            return;
        }

        const start = new Date(startDate);
        const end = new Date(endDate);
        
        if (end < start) {
            setError('End date must be after start date');
            return;
        }

        try {
            setLoading(true);
            setError('');
            
            if (editingVacation) {
                await updateTrainerVacation(editingVacation.id, start, end);
                setSuccess('Vacation updated successfully');
            } else {
                await createTrainerVacation(
                    parseInt(selectedTrainer),
                    affiliate.id,
                    start,
                    end
                );
                setSuccess('Vacation created successfully');
            }
            
            // Reset form
            if (!isTrainer) {
                setSelectedTrainer('');
            }
            setStartDate('');
            setEndDate('');
            setEditingVacation(null);
            
            // Reload vacations
            await loadVacations();
            
            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message || 'Failed to save vacation');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (vacation) => {
        setEditingVacation(vacation);
        setSelectedTrainer(vacation.trainerId.toString());
        setStartDate(new Date(vacation.startDate).toISOString().split('T')[0]);
        setEndDate(new Date(vacation.endDate).toISOString().split('T')[0]);
    };

    const handleCancelEdit = () => {
        setEditingVacation(null);
        if (!isTrainer) {
            setSelectedTrainer('');
        }
        setStartDate('');
        setEndDate('');
    };

    const handleDelete = async (vacationId) => {
        if (!window.confirm('Are you sure you want to delete this vacation?')) {
            return;
        }

        try {
            setLoading(true);
            setError('');
            await deleteTrainerVacation(vacationId);
            setSuccess('Vacation deleted successfully');
            await loadVacations();
            
            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message || 'Failed to delete vacation');
        } finally {
            setLoading(false);
        }
    };

    const canEditVacation = (vacation) => {
        if (isAffiliate) return true;
        if (isTrainer && currentUserId) {
            return vacation.trainerId === currentUserId;
        }
        return false;
    };

    // Group vacations by trainer
    const vacationsByTrainer = useMemo(() => {
        const grouped = {};
        vacations.forEach(vacation => {
            const trainerName = vacation.trainer.fullName;
            if (!grouped[trainerName]) {
                grouped[trainerName] = [];
            }
            grouped[trainerName].push(vacation);
        });
        return grouped;
    }, [vacations]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const handleClose = () => {
        handleCancelEdit();
        setError('');
        setSuccess('');
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            sx={{
                '& .MuiDialog-paper': {
                    margin: isMobile ? 0 : 32,
                    width: isMobile ? '100%' : 'auto',
                    maxHeight: isMobile ? '100%' : '90vh'
                }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        Vacations {currentYear}
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent 
                dividers
                sx={{
                    px: isMobile ? 2 : 3,
                    py: isMobile ? 2 : 3
                }}
            >
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}

                {/* Add/Edit Vacation Form */}
                <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {editingVacation ? 'Edit Vacation' : 'Add New Vacation'}
                    </Typography>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={isMobile ? 12 : 4}>
                            <FormControl fullWidth disabled={isTrainer || editingVacation !== null}>
                                <InputLabel>Trainer</InputLabel>
                                <Select
                                    value={selectedTrainer}
                                    label="Trainer"
                                    onChange={(e) => setSelectedTrainer(e.target.value)}
                                    required
                                >
                                    {trainers.map((trainer) => (
                                        <MenuItem key={trainer.trainerId} value={trainer.trainerId.toString()}>
                                            {trainer.fullName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={isMobile ? 12 : 3}>
                            <TextField
                                label="Start Date"
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                required
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12} sm={isMobile ? 12 : 3}>
                            <TextField
                                label="End Date"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                required
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12} sm={isMobile ? 12 : 2}>
                            <Box sx={{ display: 'flex', gap: 1, flexDirection: isMobile ? 'column' : 'row' }}>
                                <Button 
                                    type="submit" 
                                    variant="contained" 
                                    disabled={loading}
                                    fullWidth={isMobile}
                                >
                                    {editingVacation ? 'Update' : 'Add'}
                                </Button>

                                {editingVacation && (
                                    <Button 
                                        onClick={handleCancelEdit} 
                                        variant="outlined"
                                        disabled={loading}
                                        fullWidth={isMobile}
                                    >
                                        Cancel
                                    </Button>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Vacations List */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Scheduled Vacations ({currentYear})
                </Typography>

                {loading && vacations.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : vacations.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                        No vacations scheduled for {currentYear}
                    </Typography>
                ) : (
                    <Box sx={{ 
                        maxHeight: isMobile ? 'calc(100vh - 400px)' : 400, 
                        overflow: 'auto',
                        pr: isMobile ? 0 : 1
                    }}>
                        {Object.entries(vacationsByTrainer).map(([trainerName, trainerVacations]) => (
                            <Box key={trainerName} sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                                    {trainerName}
                                </Typography>
                                <List dense={!isMobile}>
                                    {trainerVacations.map((vacation) => (
                                        <Box
                                            key={vacation.id}
                                            sx={{
                                                border: '1px solid #e0e0e0',
                                                borderRadius: 1,
                                                mb: 1.5,
                                                p: isMobile ? 1.5 : 1,
                                                backgroundColor: editingVacation?.id === vacation.id ? '#f5f5f5' : 'transparent'
                                            }}
                                        >
                                            <Box sx={{ 
                                                display: 'flex', 
                                                flexDirection: 'row',
                                                justifyContent: 'space-between',
                                                alignItems: isMobile ? 'flex-start' : 'center',
                                                gap: 1
                                            }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        gap: 1, 
                                                        alignItems: 'center',
                                                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                                                        mb: 0.5
                                                    }}>
                                                        <Chip 
                                                            label={formatDate(vacation.startDate)} 
                                                            size="small" 
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>to</Typography>
                                                        <Chip 
                                                            label={formatDate(vacation.endDate)} 
                                                            size="small" 
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {Math.ceil((new Date(vacation.endDate) - new Date(vacation.startDate)) / (1000 * 60 * 60 * 24)) + 1} days
                                                    </Typography>
                                                </Box>
                                                
                                                {canEditVacation(vacation) && (
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        gap: 0.5,
                                                        flexShrink: 0,
                                                        alignSelf: isMobile ? 'flex-start' : 'center',
                                                        marginTop: isMobile ? 0 : 0
                                                    }}>
                                                        <IconButton 
                                                            onClick={() => handleEdit(vacation)}
                                                            disabled={loading}
                                                            size="small"
                                                            sx={{ p: 0.5 }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton 
                                                            onClick={() => handleDelete(vacation.id)}
                                                            disabled={loading}
                                                            size="small"
                                                            sx={{ 
                                                                p: 0.5,
                                                                backgroundColor: 'error.main',
                                                                color: 'white',
                                                                '&:hover': {
                                                                    backgroundColor: 'error.dark',
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" sx={{ color: 'white' }} />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </List>
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

